// src/services/browserSession.js
//
// Ends the login when the browser is closed.
//
// Tokens live in localStorage so that every tab shares one login, but
// localStorage also survives the browser closing. A page cannot reliably tell
// "browser closing" apart from "tab reloading" while it unloads, so the check
// happens on the next page load instead:
//
//   - Same tab reloaded → its sessionStorage marker is still there and the last
//     heartbeat is only seconds old. Keep the login.
//   - Another portal tab is still open (new tab, link opened in a tab) → that
//     tab answers a BroadcastChannel ping. Keep the login.
//   - Neither → every portal tab is gone: the browser was closed, crashed, or
//     the last portal tab was closed. End the session before anything renders.
//
// The web platform gives no way to tell "last portal tab closed" apart from
// "browser closed", so both end the session.

import { endSession } from "utils/logoutHelper";
import { hasAuthState } from "services/tokenStorage";

const LAST_SEEN_KEY = "session_last_seen";
const TAB_MARKER_KEY = "session_tab_alive";
const CHANNEL_NAME = "annotation-portal-session";

// How often an open tab records that it is alive.
const HEARTBEAT_MS = 5000;
// A reload counts as "same tab still open" if the tab was alive this recently.
// Long enough for a slow network to re-download the app, short enough that a
// browser reopened with "restore tabs" is not mistaken for a reload.
const RELOAD_GRACE_MS = 30 * 1000;
// How long to wait for another open tab to answer.
const PEER_REPLY_TIMEOUT_MS = 400;
// Without BroadcastChannel (very old browsers) fall back to the heartbeat
// alone. Background tabs only run timers about once a minute, hence the margin.
const NO_CHANNEL_ALIVE_MS = 2 * 60 * 1000;

function readStorage(storage, key) {
  try {
    return storage.getItem(key);
  } catch (e) {
    // private mode / storage disabled
    return null;
  }
}

function writeStorage(storage, key, value) {
  try {
    storage.setItem(key, String(value));
  } catch (e) {
    // nothing useful to do — presence just won't be recorded
  }
}

const markAlive = () => writeStorage(localStorage, LAST_SEEN_KEY, Date.now());
const msSinceLastSeen = () => Date.now() - (Number(readStorage(localStorage, LAST_SEEN_KEY)) || 0);

let channel = null;
function getChannel() {
  if (channel || typeof BroadcastChannel === "undefined") return channel;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
  } catch (e) {
    channel = null;
  }
  return channel;
}

/* Ask other open portal tabs whether they exist. Resolves true on the first
   answer, false after the timeout. */
function anotherTabIsOpen() {
  const bc = getChannel();
  if (!bc) return Promise.resolve(msSinceLastSeen() < NO_CHANNEL_ALIVE_MS);

  return new Promise((resolve) => {
    let timer = null;

    const onMessage = (event) => {
      if (event.data && event.data.type === "pong") finish(true);
    };

    function finish(result) {
      clearTimeout(timer);
      bc.removeEventListener("message", onMessage);
      resolve(result);
    }

    timer = setTimeout(() => finish(false), PEER_REPLY_TIMEOUT_MS);
    bc.addEventListener("message", onMessage);
    bc.postMessage({ type: "ping" });
  });
}

/* Keep this tab visible to future page loads: heartbeat, answer pings, and
   stamp the time on the way out so a reload sees a fresh value. */
function startPresence() {
  writeStorage(sessionStorage, TAB_MARKER_KEY, "1");
  markAlive();

  setInterval(markAlive, HEARTBEAT_MS);
  window.addEventListener("pagehide", markAlive);
  document.addEventListener("visibilitychange", markAlive);

  const bc = getChannel();
  if (bc) {
    bc.addEventListener("message", (event) => {
      if (event.data && event.data.type === "ping") bc.postMessage({ type: "pong" });
    });
  }
}

/**
 * Run once before the app renders. Ends a login left behind by a closed
 * browser, then starts this tab's presence tracking. Never rejects.
 */
export async function initBrowserSession() {
  try {
    if (hasAuthState()) {
      const sameTabReload =
        readStorage(sessionStorage, TAB_MARKER_KEY) === "1" && msSinceLastSeen() < RELOAD_GRACE_MS;

      if (!sameTabReload && !(await anotherTabIsOpen())) {
        await endSession({ timeoutMs: 1500 });
      }
    }
  } catch (e) {
    // Never block the app from starting.
  }

  startPresence();
}
